/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { EditIcon } from '@sketchnote/icons';
import { useRouter } from 'next/router';
import * as React from 'react';
import { ColumnToolbar } from 'src/features/kanban/component/Column/Toolbar';
import { useTableStore } from 'src/features/list/store';
import useOnClickOutside from 'use-onclickoutside';

const { memo, useRef, useState } = React;

const EditColumnItem = ({ column }: { column: any }) => {
  const [isEditColumnTitle, setIsEditColumnTitle] = useState<boolean>(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [input, setInput] = useState<string>(column.name);
  const { onChangeColumnName, boardTableId, onChangeColumnColor, onDeleteColumn } = useTableStore(
    (s) => ({
      onChangeColumnName: s.onChangeColumnName,
      boardTableId: s.boardTableId,
      onChangeColumnColor: s.onChangeColumnColor,
      onDeleteColumn: s.onDeleteColumn,
    })
  );

  const handleColumnChange = (e) => {
    if (e.value === 'delete') {
      setIsDeletePromptOpen(true);
    } else if (e.value != 'colour-picker') {
      onChangeColumnColor({ id: column.id, color: e.value, boardId: boardTableId });
    }
  };

  if (isEditColumnTitle) {
    return (
      <div className="text-xs text-gray-700 font-medium bg-white border border-gray-200 rounded-sm p-6 leading-10 mr-4">
        <input
          type="text"
          value={input}
          autoFocus
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onChangeColumnName({ id: column.id, name: input, boardId: boardTableId });
              setIsEditColumnTitle(false);
            }
          }}
          onBlur={() => {
            onChangeColumnName({ id: column.id, name: input, boardId: boardTableId });
            setIsEditColumnTitle(false);
          }}
        />
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsEditColumnTitle(true)}
        className="text-xs text-gray-700 font-medium bg-gray-100 rounded-sm p-6 leading-10 mr-4 cursor-text"
        style={{ backgroundColor: column?.color || 'transparent' }}
      >
        {column.name}
      </button>
      <ColumnToolbar onClick={handleColumnChange} />
      <Modal
        isOpen={isDeletePromptOpen}
        onClose={() => {
          setIsDeletePromptOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">
            {column.items.length > 0 && 'All tasks with this status would also be deleted.'}
            Are you sure you wish to proceed?
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeletePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={() => onDeleteColumn({ columnId: column.id, boardId: boardTableId })}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

const StatusCell = ({ data: item, api }: any) => {
  const { columns, onUpdateItemPlacement, onAddNewColumn } = useTableStore((s) => ({
    columns: s.columns,
    onUpdateItemPlacement: s.onUpdateItemPlacement,
    onAddNewColumn: s.onAddNewColumn,
  }));
  const router = useRouter();
  const myRef = useRef(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isNewLabel, setIsNewLabel] = useState<boolean>(false);
  const [newColumnInput, setNewColumnInput] = useState<string>('');
  const { isReadOnly } = useTableStore();
  const handleChange = (value) => {
    const destColId = value.id;
    const sourceColId = item.colId;

    const sourceIndex =
      columns.find((i) => i.id === item.colId)?.items.findIndex((i) => i.id === item.id) || 0;

    if (sourceColId !== destColId) {
      onUpdateItemPlacement({
        destColId,
        itemId: item.id,
        sourceColId,
        sourceIndex,
        destIndex: 0,
        boardId: item.boardId,
      });
    }
  };

  useOnClickOutside(myRef, () => {
    mode === 'view' && api.stopEditing();
  });

  const changeEditorMode = () => {
    setMode('edit');
  };

  const applyChanges = () => {
    api.stopEditing();
    setMode('view');
  };

  // if (item.parent) return <span />;

  if (isReadOnly) return <span />;

  if (mode === 'edit') {
    return (
      <div className="more-menu-popover" ref={myRef}>
        <div className="bg-white p-6 flex flex-col gap-0 justify-center  min-w-200 max-h-400 overflow-y-scroll">
          {columns.map((column) => {
            return (
              <div
                key={column.id}
                className="flex justify-between w-full px-10 h-40 items-center text-gray-500"
              >
                <EditColumnItem column={column} />
              </div>
            );
          })}
          {isNewLabel ? (
            <div className="flex h-30 justify-center items-center">
              <input
                autoFocus
                type="text"
                placeholder="Add Status"
                value={newColumnInput}
                onChange={(e) => setNewColumnInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newColumnInput.length > 0) {
                    onAddNewColumn({
                      boardId: router?.query?.view,
                      color: '#DFF8E3',
                      name: newColumnInput,
                    });
                    setIsNewLabel(false);
                  }
                }}
                onBlur={() => {
                  if (newColumnInput.length > 0) {
                    onAddNewColumn({
                      boardId: router?.query?.view,
                      color: '#DFF8E3',
                      name: newColumnInput,
                    });
                    setIsNewLabel(false);
                  }
                }}
                className="text-xs w-full text-gray-700 font-medium rounded-sm p-6 leading-10 border border-gray-200"
              />
            </div>
          ) : (
            <button
              onClick={() => setIsNewLabel(true)}
              className="flex h-30 justify-center items-center hover:bg-gray-100"
            >
              <span className="text-xs text-gray-700 font-medium rounded-sm p-6 leading-10 mr-4">
                + New label
              </span>
            </button>
          )}
          <span className=" h-1 border-t border-gray-200 my-5" />
          <button
            onClick={applyChanges}
            className="flex h-30 justify-center items-center hover:bg-gray-100"
          >
            <span className="text-xs text-gray-700 font-medium rounded-sm p-6 leading-10 mr-4">
              Done
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="more-menu-popover" ref={myRef}>
      <div className="bg-white p-6 flex flex-col gap-0 justify-center  min-w-200 max-h-400 overflow-y-scroll">
        {columns.map((column) => {
          return (
            <button
              onClick={() => handleChange(column)}
              key={column.id}
              className="flex px-10 h-40 items-center  hover:bg-gray-100"
            >
              <span
                className="text-xs text-gray-700 font-medium bg-primary-200 rounded-sm p-6 leading-10 mr-4"
                style={{ backgroundColor: column?.color || 'transparent' }}
              >
                {column.name}
              </span>
            </button>
          );
        })}
        <span className=" h-1 border-t border-gray-200 my-5" />
        <button
          onClick={changeEditorMode}
          className="flex h-30 justify-center items-center hover:bg-gray-100"
        >
          <EditIcon className="mr-8 mb-2 text-gray-500" />
          <span className="text-xs text-gray-700 font-medium rounded-sm leading-10">
            Edit Labels
          </span>
        </button>
      </div>
    </div>
  );
};

export const StatusCellEditor = memo(StatusCell);
